import { determineSimulationUsage } from '../utils'
import { DeliveryAddressFieldKeys, InputGroupKeys, MeterDetailsFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import { RootState, store } from 'store'
import { GetSortedPropositionsParams, UsageStepFormInputs } from './types'
import { updateAllInputs } from 'store/customer/slice'
import { Product, PRODUCT_CONFIG_OPTIONS, PRODUCTS, PRODUCTS_CONFIG, REGIONS } from '@boltenergy-be/shared'
import { Proposition } from 'store/customer/types'
import { PROPOSITIONS_ORDER } from 'features/registration/constants'
import { EV_FLEX_SIMULATION_SO } from 'features/bolt-drive-flex/BoltDriveFlex.constants'

/**
 * Updates the store inputs for the usage step
 *
 * @param {UsageStepFormInputs} data
 */
export const updateStoreForUsageStep = (data: UsageStepFormInputs) => {
  // Fetch inputs from store
  const { inputs } = (store.getState() as RootState).customer

  // Update the store inputs
  store.dispatch(
    updateAllInputs({
      inputs: {
        ...inputs,
        [InputGroupKeys.SIMULATION]: {
          ...inputs.simulation,
          [SimulationFieldKeys.USAGE]: determineSimulationUsage(data.usageGrade, data)!,
          [SimulationFieldKeys.HAS_EXCLUSIVE_NIGHT_METER]: data.hasExclNight,
          [SimulationFieldKeys.METER_TYPE]: data.meterType,
          ...(data.hasSolarPanels && data.solarPanels && { [SimulationFieldKeys.SOLAR_PANELS]: Number(data.solarPanels ?? 0) })
        },
        [InputGroupKeys.DELIVERY_ADDRESS]: {
          ...inputs[InputGroupKeys.DELIVERY_ADDRESS],
          [DeliveryAddressFieldKeys.NEEDS_GAS]: !!data.needsGas
        },
        [InputGroupKeys.METER_DETAILS]: {
          ...inputs.meterDetails,
          [MeterDetailsFieldKeys.COUNTER_TYPE]: data.counterType,
          [MeterDetailsFieldKeys.HAS_SOLAR_PANELS]: data.hasSolarPanels,
          [MeterDetailsFieldKeys.WALLONIA_SOLAR_PANELS_SINCE_2024]:
            inputs.simulation.region === REGIONS.wallonia ? data.walloniaSolarPanelsSince2024 : undefined
        }
      }
    })
  )
}

/**
 * Returns the propositions to show on the proposition step, sorted by PROPOSITIONS_ORDER.
 * When an available specific product is requested, only that product (or its Plenty counterpart) is returned.
 *
 * @param {GetSortedPropositionsParams} params
 * @returns {[Product, Proposition | undefined][]}
 */
export const getSortedPropositions = ({
  simulatedPropositions,
  simulateSpecificProduct,
  showPlentyPropositions,
  enforcePlenty,
  usableProducts,
  simulationSo,
  hasProductsContent
}: GetSortedPropositionsParams): [Product, Proposition | undefined][] => {
  if (!simulatedPropositions) return []

  if (simulateSpecificProduct && usableProducts.includes(simulateSpecificProduct)) {
    const counterpart = PRODUCTS_CONFIG[simulateSpecificProduct][PRODUCT_CONFIG_OPTIONS.counterpart]
    const productToShow =
      showPlentyPropositions && counterpart
        ? [simulateSpecificProduct, counterpart].find((p) => PRODUCTS_CONFIG[p].isPlentyProduct) || simulateSpecificProduct
        : simulateSpecificProduct

    return [[productToShow, simulatedPropositions[productToShow]]]
  }

  if (!hasProductsContent) return []

  const order =
    showPlentyPropositions || enforcePlenty
      ? PROPOSITIONS_ORDER.map((product) => PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.counterpart])
      : PROPOSITIONS_ORDER

  return order
    .filter(
      (product): product is Product =>
        !!product &&
        usableProducts.includes(product) &&
        product in simulatedPropositions &&
        !(simulationSo === EV_FLEX_SIMULATION_SO && product === PRODUCTS.fix)
    )
    .map((product) => [product, simulatedPropositions[product]])
}
